import { useMemo, useCallback } from 'react';
import { useAppStore } from '../store';
import type { Budget } from '../types';
import { getCategoryDescendants } from '../services/categoryService';

export function useBudgets(month?: string) {
  const budgets = useAppStore((state) => state.budgets);

  return useMemo(() => {
    if (!month) return budgets;
    return budgets.filter((budget) => budget.month === month);
  }, [budgets, month]);
}

export function useBudget(categoryId: string, month: string) {
  const budgets = useAppStore((state) => state.budgets);

  return useMemo(
    () =>
      budgets.find(
        (budget) => budget.categoryId === categoryId && budget.month === month
      ),
    [budgets, categoryId, month]
  );
}

export interface BudgetSummaryItem {
  categoryId: string;
  categoryName: string;
  parentId: string | null;
  color: string;
  estimated: number;
  actual: number;
  objective?: number;
  variance: number;
  percentUsed: number;
}

export interface BudgetSummaryTotals {
  estimated: number;
  actual: number;
  variance: number;
  percentUsed: number;
}

export function useBudgetSummary(month: string) {
  const budgets = useAppStore((state) => state.budgets);
  const transactions = useAppStore((state) => state.transactions);
  const categories = useAppStore((state) => state.categories);

  return useMemo(() => {
    const monthBudgets = budgets.filter((budget) => budget.month === month);
    const monthTransactions = transactions.filter(
      (t) => t.date.slice(0, 7) === month && !t.excludedFromBudget
    );

    const categoryMap = new Map(categories.map((c) => [c.id, c]));

    const actualByCategory = new Map<string, number>();
    monthTransactions.forEach((transaction) => {
      if (transaction.categoryId) {
        const current = actualByCategory.get(transaction.categoryId) || 0;
        actualByCategory.set(
          transaction.categoryId,
          current + Math.abs(transaction.amount)
        );
      }
    });

    const budgetMap = new Map(monthBudgets.map((b) => [b.categoryId, b]));

    const processedCategoryIds = new Set<string>();
    const summary: BudgetSummaryItem[] = [];

    monthBudgets.forEach((budget) => {
      const category = categoryMap.get(budget.categoryId);
      if (!category) return;

      const descendants = getCategoryDescendants(categories, budget.categoryId);
      const descendantIds = descendants.map((d) => d.id);

      let actual = actualByCategory.get(budget.categoryId) || 0;
      descendantIds.forEach((descId) => {
        if (!budgetMap.has(descId)) {
          actual += actualByCategory.get(descId) || 0;
        }
      });

      const variance = actual - budget.estimated;
      const percentUsed =
        budget.estimated > 0 ? (actual / budget.estimated) * 100 : 0;

      summary.push({
        categoryId: budget.categoryId,
        categoryName: category.name,
        parentId: category.parentId,
        color: category.color,
        estimated: budget.estimated,
        actual,
        objective: budget.objective,
        variance,
        percentUsed,
      });

      processedCategoryIds.add(budget.categoryId);
      descendantIds.forEach((id) => {
        if (!budgetMap.has(id)) {
          processedCategoryIds.add(id);
        }
      });
    });

    actualByCategory.forEach((actual, categoryId) => {
      if (!processedCategoryIds.has(categoryId)) {
        const category = categoryMap.get(categoryId);
        summary.push({
          categoryId,
          categoryName: category?.name ?? 'Unknown',
          parentId: category?.parentId ?? null,
          color: category?.color ?? '#9E9E9E',
          estimated: 0,
          actual,
          variance: actual,
          percentUsed: 0,
        });
      }
    });

    const totalEstimated = summary.reduce(
      (sum, item) => sum + item.estimated,
      0
    );
    const totalActual = summary.reduce((sum, item) => sum + item.actual, 0);

    return {
      items: summary,
      totals: {
        estimated: totalEstimated,
        actual: totalActual,
        variance: totalActual - totalEstimated,
        percentUsed:
          totalEstimated > 0 ? (totalActual / totalEstimated) * 100 : 0,
      } as BudgetSummaryTotals,
    };
  }, [budgets, transactions, categories, month]);
}

export function useSetBudget() {
  const budgets = useAppStore((state) => state.budgets);
  const setBudget = useAppStore((state) => state.setBudget);

  return useCallback(
    (data: Omit<Budget, 'id'> | Budget) => {
      if ('id' in data && data.id) {
        setBudget(data as Budget);
      } else {
        const existing = budgets.find(
          (b) => b.categoryId === data.categoryId && b.month === data.month
        );
        if (existing) {
          setBudget({ ...existing, ...data });
        } else {
          const id = `budget-${data.categoryId}-${data.month}`;
          setBudget({ ...data, id });
        }
      }
    },
    [budgets, setBudget]
  );
}

export function useDeleteBudget() {
  const deleteBudget = useAppStore((state) => state.deleteBudget);

  return useCallback(
    (id: string) => {
      deleteBudget(id);
    },
    [deleteBudget]
  );
}
